// ProjectStore.ts
import { makeAutoObservable, runInAction } from 'mobx';
import { IProject } from "../types/Project";
import { ProjectManager } from '../models';
import {
  cycleAvailabilityStatus,
  getAvailabilityFieldsForStatus,
  getProjectAvailabilityStatus,
} from '../utilities/projectAvailability';

export class ProjectStore {
  selectedProject: string = localStorage.getItem('selectedProject') || '';
  projects: IProject[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedProject(project: string) {
    this.selectedProject = project;
    localStorage.setItem('selectedProject', project);
  }

  getSelectedProject() {
    return this.selectedProject;
  }

  addProject(newProject: IProject) {
    runInAction(() => {
      this.projects.push(newProject);
    });
  }

  setProjects(projects: IProject[]) {
    runInAction(() => {
      this.projects = projects;
    });
  }

  async fetchProjects() {
    this.loading = true;
    this.error = null;
    try {
      const projectData = await ProjectManager.observeProjects();
      runInAction(() => {
        this.projects = projectData;
        if (!this.selectedProject && this.projects.length > 0) {
          this.setSelectedProject(this.projects[0].project_id);
        }
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        console.error('Error fetching projects:', error);
        this.error = error instanceof Error ? error.message : 'Error fetching projects';
        this.loading = false;
      });
    }
  }

  deleteProject = (project_id: string) => {
    runInAction(() => {
      this.projects = this.projects.filter(project => project.project_id !== project_id);
    });
  }

  async updateProject(project_id: string, updatedProject: IProject) {
    try {
      const index = this.projects.findIndex(project => project.project_id === project_id);
      if (index !== -1) {
        runInAction(() => {
          this.projects[index] = { ...this.projects[index], ...updatedProject };
        });
      } else {
        throw new Error('Project not found');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  async cycleProjectAvailability(project_id: string) {
    try {
      let project = this.projects.find(p => p.project_id === project_id);

      if (!project) {
        const fetchedProject = await ProjectManager.getProject(project_id);
        if (!fetchedProject) {
          throw new Error('Project not found');
        }
        runInAction(() => {
          this.projects.push(fetchedProject);
        });
        project = fetchedProject;
      }

      if (!project) {
        throw new Error('Project not found after fetch attempt');
      }

      const currentStatus = getProjectAvailabilityStatus(project);
      const nextStatus = cycleAvailabilityStatus(currentStatus);
      const nextFields = getAvailabilityFieldsForStatus(nextStatus);

      runInAction(() => {
        const index = this.projects.findIndex(p => p.project_id === project_id);
        if (index !== -1) {
          this.projects[index] = {
            ...this.projects[index],
            ...nextFields,
          };
        }
      });

      try {
        await ProjectManager.updateProjectAvailabilityStatus(project_id, nextStatus);
      } catch (error) {
        runInAction(() => {
          const index = this.projects.findIndex(p => p.project_id === project_id);
          if (index !== -1) {
            this.projects[index] = {
              ...this.projects[index],
              ...getAvailabilityFieldsForStatus(currentStatus),
            };
          }
        });
        console.error('Error cycling project availability:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error cycling project availability:', error);
      throw error;
    }
  }

  /** @deprecated Use cycleProjectAvailability */
  async toggleProjectAvailability(project_id: string) {
    return this.cycleProjectAvailability(project_id);
  }
}

export const projectStore = new ProjectStore();
projectStore.fetchProjects(); // Fetch projects when the app singleton is created
